import type { AgentRunStatus, FieldConfig } from '@lucidtech/las-sdk-browser';
import { FormApi, ReactFormApi } from '@tanstack/react-form';
import { createContext } from 'react';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export type FieldValueState = {
  id?: string;
  confidence?: number;
  value?: string | null;
  rawValue?: string | null;
  attentionMap?: number[][];
  isValidated?: boolean;
  page?: number;
  name?: string;
  onChange?: (value: string) => void;
};

export type FormValue = {
  current: FieldValueState;
  candidates: FieldValueState[];
};

export type FormValueRow = {
  id: string;
  columns: { [key: string]: FormValue };
};

export type FormValues = {
  [key: string]: FormValueRow[] | FormValue;
};

export type FormType = FormApi<FormValues, undefined> & ReactFormApi<FormValues, undefined>;
export type CreateDocumentStoreProps = Partial<DocumentProps>;
export type Point = {
  x: number;
  y: number;
};
export type AttentionBlock = {
  intensity: number;
  points: Point[];
};

export interface DocumentProps {
  documentId?: string;
  annotationFileUrl?: string;
  predictionId?: string;
  documentBlob?: Blob;
  zoom: number;
  rotation: number;
  numPages?: number;
  currentPages: number[];
  focusedField?: FieldValueState;
  attention?: AttentionBlock[];
  fieldConfig?: FieldConfig;
  contentText: { [key: number]: string[] };
  maximizedFields: string[];
  status?: AgentRunStatus;
  attemptedSubmit?: boolean;
  isLoading: boolean;
  isEditing?: boolean;
  predictedPages?: number[];
  showPages?: number[];
}

export interface DocumentState extends DocumentProps {
  clear: () => void;
  setIsLoading: (loading: boolean | ((prev: boolean) => boolean)) => void;
  setDocumentId: (documentId?: string) => void;
  setAnnotationFileUrl: (url?: string) => void;
  setDocumentBlob: (blob?: Blob) => void;
  setFieldConfig: (fieldConfig?: FieldConfig) => void;
  setStatus: (status?: string) => void;
  setZoom: (change: ((prev: number) => number) | number) => void;
  setRotation: (change: (prev: number) => number) => void;
  setNumPages: (numPages?: number) => void;
  setCurrentPages: (currentPages: number[]) => void;
  updateContentText: (page: number, text: string[]) => void;
  getAutocomplete: () => string[];
  setMaximizedFields: (maximizedFields: string[]) => void;
  setFocusedField: (fieldValue?: FieldValueState) => void;
  setAttention: (attention?: AttentionBlock[]) => void;
  setAttemptedSubmit: (attemptedSubmit: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
  setPredictedPages: (predictedPages: number[] | undefined) => void;
  setShowPages: (showPages: number[] | undefined) => void;
}

export const createDocumentStore = (initProps: CreateDocumentStoreProps) => {
  const props: DocumentProps = {
    zoom: 1,
    rotation: 0,
    currentPages: [],
    maximizedFields: [],
    contentText: {},
    isEditing: false,
    ...initProps,
  };
  return createStore<DocumentState>()(
    // use persist when we figure a way to serialize the form state
    devtools(
      immer((set, get) => ({
        ...props,
        setIsLoading: (loading: boolean | ((prev: boolean) => boolean)) =>
          set((state) => {
            if (typeof loading === 'function') {
              state.isLoading = loading(state.isLoading);
            } else {
              state.isLoading = loading;
            }
          }),
        clear: () =>
          set((state) => {
            state.documentId = undefined;
            state.documentBlob = undefined;
            state.predictionId = undefined;
            state.focusedField = undefined;
            state.attention = undefined;
            state.annotationFileUrl = undefined;
            state.attemptedSubmit = false;
            state.currentPages = [];
            state.maximizedFields = [];
            state.contentText = {};
            state.rotation = 0;
            state.zoom = 1;
            state.form?.reset({}, { keepDefaultValues: false });
            state.isEditing = false;
            state.predictedPages = undefined;
            state.showPages = undefined;
          }),
        setZoom: (change: ((prev: number) => number) | number) =>
          set((state) => {
            if (typeof change === 'function') {
              state.zoom = change(state.zoom);
            } else {
              state.zoom = change;
            }
          }),
        setDocumentId: (documentId?: string) =>
          set((state) => {
            state.documentId = documentId;
          }),
        setAnnotationFileUrl: (url?: string) =>
          set((state) => {
            state.annotationFileUrl = url;
          }),
        setDocumentBlob: (blob?: Blob) =>
          set((state) => {
            state.documentBlob = blob;
          }),
        setFieldConfig: (fieldConfig?: FieldConfig) =>
          set((state) => {
            state.fieldConfig = fieldConfig;
          }),
        setStatus: (status?: string) =>
          set((state) => {
            state.status = status as AgentRunStatus | undefined;
          }),
        setRotation: (change: (prev: number) => number) =>
          set((state) => {
            state.rotation = change(state.rotation);
          }),
        setNumPages: (numPages?: number) =>
          set((state) => {
            state.numPages = numPages;
          }),
        setCurrentPages: (currentPages: number[]) =>
          set((state) => {
            state.currentPages = currentPages;
          }),
        updateContentText: (page: number, text: string[]) =>
          set((state) => {
            state.contentText[page] = text;
          }),
        getAutocomplete: () => {
          let autocomplete = Object.values(get().contentText).reduce((a, b) => a.concat(b), []);
          autocomplete = [...new Set(autocomplete)];
          autocomplete.sort();
          return autocomplete;
        },
        setMaximizedFields: (maximizedFields: string[]) =>
          set((state) => {
            state.maximizedFields = maximizedFields;
          }),
        setFocusedField: (focusedField?: FieldValueState) =>
          set((state) => {
            state.focusedField = focusedField;
          }),
        setAttention: (attention?: AttentionBlock[]) =>
          set((state) => {
            state.attention = attention;
          }),
        setAttemptedSubmit: (attemptedSubmit: boolean) =>
          set((state) => {
            state.attemptedSubmit = attemptedSubmit;
          }),
        setIsEditing: (isEditing: boolean) =>
          set((state) => {
            state.isEditing = isEditing;
          }),
        setPredictedPages: (predictedPages: number[] | undefined) =>
          set((state) => {
            state.predictedPages = predictedPages;
          }),
        setShowPages: (showPages: number[] | undefined) =>
          set((state) => {
            state.showPages = showPages;
          }),
      }))
    )
  );
};

export type DocumentStore = ReturnType<typeof createDocumentStore>;

export const DocumentContext = createContext<DocumentStore | null>(null);
